import React from "react";
import { connect } from "react-redux";

import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import {
    Alignment,
    FlexBehaviour,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Croppie from "../core/components/cropping/Cropping";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import ContextFragment from "../core/components/contextmenu/ContextFragment";
import ContextMenuItem from "../core/components/contextmenu/ContextMenuItem";
import User from "../core/types/core/User";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";
import ClientSystem from "../core/types/core/ClientSystem";
import {
    deleteClientSystems,
    initialiseClientSystems,
    newClientSystem,
    reissueCertificate,
    updateClientSystem,
} from "../core/state/core/ClientSystem";
import Panel from "../core/components/panels/Panel";
import Dialog from "../core/components/dialog/Dialog";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import DialogCancel from "../core/components/dialog/DialogCancel";
import Spacer from "../core/components/containers/Spacer";
import DialogContent from "../core/components/dialog/DialogContent";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import InputForm from "../core/components/inputform/InputForm";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormField from "../core/components/inputform/InputFormField";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import Utilities from "../core/foundation/Utility";
import DropDown, {
    DropDownOption,
} from "../core/components/inputform/DropDown";
import {
    PermissionsScope,
    PermissionsType,
    RestrictedPermissions,
} from "../core/types/core/PermissionSet";
import Utility from "../core/foundation/Utility";
import PermissionSet from "../core/types/core/PermissionSet";

import "./Applications.scss";

interface PermissionEdit {
    index: number;
    display: string;
    readPermission: string;
    writePermission: string;
}

interface ApplicationsProps {
    user: User;
    clientSystems: ClientSystem[];

    setPageLoading: (loading: boolean) => void;
    initialiseClientSystems: () => Promise<void>;
    updateClientSystem: (clientSystem: ClientSystem) => Promise<void>;
    reissueCertificate: (clientSystem: ClientSystem) => Promise<void>;
    deleteClientSystems: (clientSystems: ClientSystem[]) => Promise<void>;
}

interface ApplicationsState {
    visibleSystems?: ClientSystem[];
    selected?: ClientSystem;
    permissionTypes: DropDownOption[];

    editOpen?: boolean;
    editLoad?: boolean;
    editTitle?: string;
    editing?: ClientSystem;

    pictureOpen?: boolean;
    pictureLoading?: boolean;
    pictureWorking?: string;
    pictureCropped?: string;
    pictureSaveAllowed?: boolean;

    permissionsOpen?: boolean;
    permissionsLoading?: boolean;
    editPermissions?: PermissionEdit[];
    readAllPermissions?: string;
    writeAllPermissions?: string;
}

class Applications extends React.Component<
    ApplicationsProps,
    ApplicationsState
> {
    state: ApplicationsState = {
        permissionTypes: [
            { value: "N", text: "", order: 0 },
            {
                value: "S",
                text: "Single",
                order: 1,
            },
            {
                value: "M",
                text: "Multi",
                order: 2,
            },
            {
                value: "A",
                text: "Admin",
                order: 3,
            },
        ],
    };

    static getDerivedStateFromProps(
        props: ApplicationsProps,
        state: ApplicationsState,
    ) {
        return {
            visibleSystems: props.clientSystems?.filter(
                item => !!item.schoolID,
            ),
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await this.props.initialiseClientSystems();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: newClientSystem(),
            editOpen: true,
            editLoad: false,
            editTitle: "Add Application Integration",
        });
    }

    edit(clientSystem?: ClientSystem) {
        this.setState({
            selected: clientSystem ?? this.state.selected,
            editing: structuredClone(clientSystem ?? this.state.selected),
            editOpen: true,
            editLoad: false,
            editTitle: "Edit Application Integration",
        });
    }

    editLogo() {
        this.setState(() => ({
            pictureWorking: this.state.editing?.logoFile,
            pictureOpen: true,
        }));
    }

    async editLogoChanged(files: FileList) {
        let fileUrl = await Utilities.fileTobase64(files[0]);
        this.setState(() => ({ pictureWorking: fileUrl }));
    }

    editLogoCropStarted() {
        this.setState(() => ({ pictureSaveAllowed: false }));
    }

    editLogoCropped(image: string) {
        this.setState(() => ({
            pictureCropped: image,
            pictureSaveAllowed: true,
        }));
    }

    saveEditLogo() {
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                logoFile: this.state.pictureCropped?.toDataUrl(),
            },
            pictureOpen: false,
        }));
    }

    editPermissions() {
        const permissionSet = new PermissionSet();
        permissionSet.parse(this.state.editing);
        const permissions = Utility.enumToOptions(PermissionsType, "number");
        const restricted = Utility.enumToOptions(
            RestrictedPermissions,
            "number",
        );
        this.setState(() => ({
            editPermissions: permissions
                .filter(item => !restricted.some(r => r.value === item.value))
                .map(item => ({
                    index: item.value,
                    display: item.text,
                    readPermission: permissionSet.getPermissionScopeRead(
                        item.value,
                    ),
                    writePermission: permissionSet.getPermissionScopeWrite(
                        item.value,
                    ),
                })),
            permissionsOpen: true,
        }));
    }

    saveEditPermissions() {
        const permissionSet = new PermissionSet();
        permissionSet.resetPermissions();
        for (const item of this.state.editPermissions) {
            const read = item.readPermission;
            const write = item.writePermission;
            permissionSet.setPermissions(
                item.index,
                PermissionsScope.Single,
                read === PermissionsScope.Single,
                write === PermissionsScope.Single,
            );
            permissionSet.setPermissions(
                item.index,
                PermissionsScope.Multi,
                read === PermissionsScope.Multi,
                write === PermissionsScope.Multi,
            );
            permissionSet.setPermissions(
                item.index,
                PermissionsScope.Admin,
                read === PermissionsScope.Admin,
                write === PermissionsScope.Admin,
            );
        }
        const rawValues = permissionSet.exportPermissions();

        this.setState(prevState => ({
            editing: {
                ...prevState.editing,
                ...rawValues,
            },
            permissionsOpen: false,
        }));
    }

    async reissueCertificate() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "This will break SAML SSO for all apps which are correctly " +
                "validating the SAML signature and are not updated with the " +
                "new certificates thumbprint / fingerprint, proceed?",
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.setState({ editLoad: true });

                await this.props.reissueCertificate(this.state.editing);

                const updated = this.props.clientSystems.find(
                    item => item.id === this.state.editing?.id,
                );
                this.setState({
                    editing: {
                        ...this.state.editing,
                        certificateExpiry: updated?.certificateExpiry,
                        certificateFingerprint: updated?.certificateFingerprint,
                    },
                });
            } finally {
                this.setState({ editLoad: false });
            }
        }
    }

    cancelEdit() {
        this.setState({
            editing: undefined,
            editOpen: false,
            editLoad: false,
            editTitle: undefined,
        });
    }

    canSave(): boolean {
        return (
            !!this.state.editing?.name &&
            !!this.state.editing?.identificationToken &&
            !!this.state.editing?.authenticationToken
        );
    }

    async saveEdit() {
        try {
            this.setState({ editLoad: true });

            await this.props.updateClientSystem(this.state.editing);

            this.setState({ editOpen: false });
        } finally {
            this.setState({ editLoad: false });
        }
    }

    async delete(clientSystem?: ClientSystem) {
        clientSystem = clientSystem ?? this.state.selected;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to delete these client systems?<br />" +
                `<ul class="indented chevron"><li>${clientSystem?.name}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await this.props.deleteClientSystems([clientSystem]);
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <>
                <section data-contextkey="timetables-menu">
                    <MainToolbarContents>
                        <ToolbarButton
                            icon="add"
                            text="Add"
                            onClick={() => this.add()}
                        />
                        <ToolbarButton
                            icon="edit"
                            text="Edit"
                            disabled={!this.state.selected}
                            onClick={() => this.edit()}
                        />
                        <ToolbarButton
                            icon="delete"
                            text="Delete"
                            disabled={!this.state.selected}
                            onClick={() => this.delete()}
                        />
                    </MainToolbarContents>

                    {this.state.visibleSystems?.length > 0 ? (
                        <GeneralContainer
                            scaling={true}
                            mid={true}
                            max={true}
                            bufferSide={Side.All}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            alignment={Alignment.Center}>
                            <List<ClientSystem>
                                joined={true}
                                selectionChanged={item =>
                                    this.setState({ selected: item })
                                }>
                                {this.state.visibleSystems.map(
                                    (file, index) => (
                                        <ListItem<ClientSystem>
                                            key={index}
                                            data={file}
                                            panel={true}
                                            autoHeight={true}
                                            selectable={true}
                                            bufferSide={Side.All}
                                            contextkey="timetables-file"
                                            contextvalue={String(file.id)}
                                            onDoubleClick={() =>
                                                this.edit(file)
                                            }>
                                            <GeneralContainer
                                                fullWidth={true}
                                                flex={FlexDirection.Row}
                                                flexWrapped={true}>
                                                <div className="col flex-column single-line">
                                                    <div className="twothirds">
                                                        <h3 className="truncated">
                                                            {file.name}
                                                        </h3>
                                                    </div>

                                                    <span className="inline top-clearance-large">
                                                        <label className="title">
                                                            Identifier:
                                                        </label>
                                                        <span className="text-selectable">
                                                            {file.identificationToken.toUpperCase()}
                                                        </span>
                                                    </span>

                                                    <span className="inline">
                                                        <label className="title">
                                                            Secret:
                                                        </label>
                                                        <span className="text-selectable">
                                                            {file.authenticationToken.toUpperCase()}
                                                        </span>
                                                    </span>
                                                </div>

                                                <Panel className="col-pane flex-column single-line">
                                                    <div className="flex-row">
                                                        <h4 className="inline">
                                                            Certificate
                                                        </h4>

                                                        <span className="inline right text-right">
                                                            <label className="title">
                                                                Expiry:
                                                            </label>
                                                            {file.certificateExpiry
                                                                .toDate()
                                                                .toLocale()}
                                                        </span>
                                                    </div>

                                                    <span className="top-clearance break-all text-selectable">
                                                        {file.certificateFingerprint.replace(
                                                            /-/g,
                                                            "",
                                                        )}
                                                    </span>
                                                </Panel>
                                            </GeneralContainer>
                                        </ListItem>
                                    ),
                                )}
                            </List>
                        </GeneralContainer>
                    ) : (
                        <Text clearanceSide={Side.All}>
                            No application integrations found.
                        </Text>
                    )}
                </section>

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={700}>
                    <DialogContent>
                        <Panel
                            flex={FlexDirection.Row}
                            flexSpaced={true}>
                            <div className="app-logo-wrapper enabled">
                                <img
                                    className="app-logo"
                                    alt="App Logo"
                                    src={
                                        this.state.editing?.logoFile ??
                                        "/images/placeholder.png"
                                    }
                                />
                                <div
                                    className="select-mask"
                                    onClick={() => this.editLogo()}></div>
                            </div>

                            <GeneralContainer
                                flex={FlexDirection.Column}
                                flexBehaviour={FlexBehaviour.Max}>
                                <Panel title="General Details">
                                    <InputForm<ClientSystem>
                                        type={ClientSystem}
                                        binding={this.state.editing}
                                        display={RenderOrientations.Vertical}
                                        onChange={value =>
                                            this.setState({ editing: value })
                                        }>
                                        <InputFormField
                                            label="Name"
                                            field="name"
                                            mandatory={true}
                                        />
                                        <InputFormField
                                            type={InputFieldTypes.TextMulti}
                                            label="Description"
                                            field="description"
                                        />
                                    </InputForm>
                                    <InputForm<ClientSystem>
                                        type={ClientSystem}
                                        binding={this.state.editing}
                                        display={RenderOrientations.Horizontal}
                                        onChange={value =>
                                            this.setState({ editing: value })
                                        }
                                        columns={2}>
                                        <InputFormField
                                            label="Application Identifier"
                                            field="identificationToken"
                                            mandatory={true}
                                        />
                                        <InputFormField
                                            label="Application Shared Secret"
                                            field="authenticationToken"
                                            mandatory={true}
                                        />
                                    </InputForm>
                                </Panel>

                                <Panel
                                    clearanceSide={Side.Top}
                                    title="Single Sign-On Paths">
                                    <InputForm<ClientSystem>
                                        type={ClientSystem}
                                        binding={this.state.editing}
                                        display={RenderOrientations.Vertical}
                                        onChange={value =>
                                            this.setState({ editing: value })
                                        }>
                                        <InputFormField
                                            label="Application Path"
                                            field="applicationPath"
                                        />
                                        <InputFormField
                                            label="Application Login Path"
                                            field="applicationLoginPath"
                                        />
                                        <InputFormField
                                            label="Application Response Path"
                                            field="applicationResponsePath"
                                        />
                                        <InputFormField
                                            type={InputFieldTypes.Check}
                                            label="Strict Path Enforcement"
                                            field="enforceStrictPaths"
                                            reverse={true}
                                        />
                                    </InputForm>
                                </Panel>

                                {this.state.editing?.id > 0 && (
                                    <Panel
                                        clearanceSide={Side.Top}
                                        flex={FlexDirection.Column}
                                        title="Signing Certificate">
                                        <GeneralContainer
                                            clearanceSide={Side.Top}
                                            flex={FlexDirection.Row}
                                            flexWrapped={true}>
                                            <Text className="col">
                                                {
                                                    this.state.editing
                                                        ?.certificateFingerprint
                                                }
                                            </Text>
                                            <Text className="col-pane single-line">
                                                {this.state.editing?.certificateExpiry
                                                    ?.toDate()
                                                    ?.toLocaleShortDate()}
                                            </Text>
                                        </GeneralContainer>
                                    </Panel>
                                )}
                            </GeneralContainer>
                        </Panel>

                        <InputFormMandatory />
                    </DialogContent>
                    <DialogFooter full={true}>
                        <DialogCancel
                            text="Edit Permissions"
                            onClick={this.editPermissions.bind(this)}
                        />
                        {this.state.editing?.id > 0 && (
                            <DialogCancel
                                text="Reissue Certificate"
                                onClick={this.reissueCertificate.bind(this)}
                            />
                        )}

                        <Spacer />

                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            secure={true}
                            disabled={!this.canSave()}
                            onClick={this.saveEdit.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.pictureOpen}
                    showLoading={this.state.pictureLoading}
                    dialogIcon="image"
                    dialogTitle="Edit Application Logo"
                    zIndex={1000}>
                    <DialogContent>
                        <Panel bufferSide={Side.All}>
                            <table style={{ width: "100%" }}>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Croppie
                                                open={this.state.pictureOpen}
                                                url={this.state.pictureWorking}
                                                square={true}
                                                imageCropStarted={() =>
                                                    this.editLogoCropStarted()
                                                }
                                                imageCropped={image =>
                                                    this.editLogoCropped(image)
                                                }
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="application-logo-wrapper">
                                                <label tabIndex={0}>
                                                    Choose File
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={ev =>
                                                            this.editLogoChanged(
                                                                ev.target.files,
                                                            )
                                                        }
                                                    />
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogOK
                            text="Save"
                            disabled={!this.state.pictureSaveAllowed}
                            onClick={() => this.saveEditLogo()}></DialogOK>
                        <DialogCancel
                            onClick={() =>
                                this.setState(() => ({ pictureOpen: false }))
                            }></DialogCancel>
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.permissionsOpen}
                    showLoading={this.state.permissionsLoading}
                    dialogTitle="Edit Application Permissions"
                    canResize={false}
                    zIndex={1000}
                    width={400}>
                    <DialogContent>
                        <GeneralContainer
                            fullWidth={true}
                            flexSpaced={true}
                            flex={FlexDirection.Column}>
                            <GeneralContainer
                                flexSpaced={true}
                                bufferSide={Side.Right}
                                bufferSize={Size.Medium}
                                flex={FlexDirection.Row}>
                                <Text className="half inline" />
                                <Text className="onequarter inline">Read</Text>
                                <Text className="onequarter inline">Write</Text>
                            </GeneralContainer>
                            <GeneralContainer
                                flexSpaced={true}
                                bufferSide={Side.Right}
                                bufferSize={Size.Medium}
                                flex={FlexDirection.Row}>
                                <Text className="half inline">All</Text>
                                <DropDown
                                    noType={true}
                                    className="onequarter"
                                    value={this.state.readAllPermissions}
                                    onChange={value =>
                                        this.setState(prevState => ({
                                            editPermissions:
                                                prevState.editPermissions.map(
                                                    item => ({
                                                        ...item,
                                                        readPermission: value,
                                                    }),
                                                ),
                                            readAllPermissions: value,
                                        }))
                                    }
                                    options={this.state.permissionTypes}
                                />
                                <DropDown
                                    noType={true}
                                    className="onequarter"
                                    value={this.state.writeAllPermissions}
                                    onChange={value =>
                                        this.setState(prevState => ({
                                            editPermissions:
                                                prevState.editPermissions.map(
                                                    item => ({
                                                        ...item,
                                                        writePermission: value,
                                                    }),
                                                ),
                                            writeAllPermissions: value,
                                        }))
                                    }
                                    options={this.state.permissionTypes}
                                />
                            </GeneralContainer>
                            {this.state.editPermissions?.map(item => (
                                <GeneralContainer
                                    key={item.index}
                                    flexSpaced={true}
                                    bufferSide={Side.Right}
                                    bufferSize={Size.Medium}
                                    flex={FlexDirection.Row}>
                                    <Text className="half inline">
                                        {item.display}
                                    </Text>
                                    <DropDown
                                        noType={true}
                                        className="onequarter"
                                        value={item.readPermission}
                                        onChange={value =>
                                            this.setState(prevState => {
                                                const editPermissions = [
                                                    ...prevState.editPermissions,
                                                ];
                                                editPermissions.find(
                                                    ep =>
                                                        ep.index === item.index,
                                                ).readPermission = value;
                                                return {
                                                    editPermissions,
                                                };
                                            })
                                        }
                                        options={this.state.permissionTypes}
                                    />
                                    <DropDown
                                        noType={true}
                                        className="onequarter"
                                        value={item.writePermission}
                                        onChange={value =>
                                            this.setState(prevState => {
                                                const editPermissions = [
                                                    ...prevState.editPermissions,
                                                ];
                                                editPermissions.find(
                                                    ep =>
                                                        ep.index === item.index,
                                                ).writePermission = value;
                                                return {
                                                    editPermissions,
                                                };
                                            })
                                        }
                                        options={this.state.permissionTypes}
                                    />
                                </GeneralContainer>
                            ))}
                        </GeneralContainer>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            onClick={() =>
                                this.setState(() => ({
                                    permissionsOpen: false,
                                }))
                            }></DialogCancel>
                        <DialogOK
                            text="Save"
                            onClick={() =>
                                this.saveEditPermissions()
                            }></DialogOK>
                    </DialogFooter>
                </Dialog>

                <ContextFragment name="applications-menu">
                    <ContextMenuItem
                        id="applications_spacer_0"
                        separator={true}
                        contextKey="applications-file"
                        order={0}
                    />
                    <ContextMenuItem
                        id="applications_delete"
                        icon="delete"
                        name="Delete"
                        contextKey="applications-file"
                        handler={(e, c) =>
                            this.delete(
                                this.props.clientSystems.find(
                                    item => String(item.id) === c.getValue(),
                                ),
                            )
                        }
                        order={4}
                    />
                </ContextFragment>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { clientSystem } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        clientSystems: clientSystem.clientSystems,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseClientSystems: () => initialiseClientSystems(),
        updateClientSystem: (clientSystem: ClientSystem) =>
            unwrappedDispatch(updateClientSystem(clientSystem)),
        reissueCertificate: (clientSystem: ClientSystem) =>
            unwrappedDispatch(reissueCertificate(clientSystem)),
        deleteClientSystems: (clientSystems: ClientSystem[]) =>
            unwrappedDispatch(deleteClientSystems(clientSystems)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Applications);
